/**
 * Opt-in, namespaced debug logging.
 *
 * A number of components/selectors carry deliberate instrumentation that logs on
 * every render or every selector recompute (e.g. GuardedRoute's `[login-flow]`
 * trace, the calendar selector event-processing logs). Left unconditional these
 * flood the console and cost real main-thread time on hot paths.
 *
 * Rather than delete the instrumentation (it is recent and useful for debugging
 * auth/calendar issues), we gate it behind a localStorage flag — mirroring the
 * R2 `BT_PACER_DIAG` pattern. Enable at runtime WITHOUT a rebuild, from the page
 * or extension console:
 *
 *   localStorage.BT_DEBUG = '1'                  // enable ALL namespaces
 *   localStorage.BT_DEBUG = 'login-flow'         // enable one namespace
 *   localStorage.BT_DEBUG = 'login-flow,calendar-selectors'  // several (comma-sep)
 *
 * The flag is read once at module init (matching BT_PACER_DIAG): toggling it takes
 * effect on the next reload. When disabled, `debugLog` is a couple of cheap guard
 * checks and a no-op — no string building, no console call.
 */

type DebugFlag = 'all' | Set<string> | null;

function parseFlag(): DebugFlag {
  try {
    const raw = localStorage.getItem('BT_DEBUG');
    if (!raw) return null;
    const trimmed = raw.trim();
    if (trimmed === '1' || trimmed === 'true' || trimmed === '*') return 'all';
    const set = new Set(trimmed.split(',').map((s) => s.trim()).filter(Boolean));
    return set.size ? set : null;
  } catch {
    return null;
  }
}

const enabled: DebugFlag = parseFlag();

export function isDebugEnabled(namespace: string): boolean {
  if (enabled === null) return false;
  if (enabled === 'all') return true;
  return enabled.has(namespace);
}

/**
 * Log under `namespace` iff that namespace is enabled via the BT_DEBUG flag.
 * Args are only touched when enabled, so passing objects/expensive strings is
 * free when the flag is off.
 */
export function debugLog(namespace: string, ...args: unknown[]): void {
  if (isDebugEnabled(namespace)) {
    // eslint-disable-next-line no-console
    console.log(`[${namespace}]`, ...args);
  }
}
